import { publicAssetUrl } from "@/lib/public-image-url";
import type { UseCaseGuide } from "./types";

const UPDATED = "2026-05-14";

export const USE_CASE_GUIDES: UseCaseGuide[] = [
  {
    slug: "wedding-car-hire",
    title: "Classic Wedding Car Hire: Complete Guide",
    shortName: "Wedding car hire",
    metaTitle: "Classic Wedding Car Hire: How It Works, What to Book, What It Costs",
    metaDescription:
      "Reference guide to hiring a classic wedding car: chauffeured vs self-drive, the most-booked models, package contents, costs, and how to time the booking around the ceremony.",
    tldr:
      "Wedding car hire is the largest single use case on BookAclassic. Most ceremonies hire one or two cars for 3–4 hours, chauffeur-driven, including ribbons and an umbrella. The most-booked models are the Rolls-Royce Silver Shadow, Bentley S2, Jaguar E-Type, Mercedes Pagoda, classic Mini, and Fiat 500 depending on the city.",
    hero: {
      image: publicAssetUrl("cars/1044708/legacy-1135861_1280w.webp"),
      alt: "1935 Bentley wedding car listed by its owner",
    },
    sections: [
      {
        heading: "Chauffeured vs self-drive on a wedding day",
        body:
          "Around 90% of wedding bookings on BookAclassic are chauffeured. Reasons: the chauffeur handles parking, photographs, the route, and any traffic problem; couples don't drive in formal wear; insurance and presentation favour the owner driving their own car. Self-drive is more common for second marriages, vow renewals, and small registry-office weddings where the couple wants to be in control.",
      },
      {
        heading: "What's typically included",
        body:
          "Most wedding packages include 3–4 hours of cover, white ribbons (or a colour to match), an umbrella for rain, a non-alcoholic drink for the couple, and one chauffeur in formal wear. Extras: a second car for the bridal party, a flower arrangement on the bonnet, or an evening transfer to the reception.",
      },
      {
        heading: "Which classic for which look",
        body:
          "British formal: a white or silver Rolls-Royce Silver Shadow or Bentley S2, offered with a chauffeur. British sporting: a Jaguar E-Type roadster for the photos, usually on a short transfer between ceremony and reception. Italian playful: a Fiat 500 or Lancia Aurelia. Continental elegant: a Mercedes-Benz Pagoda SL or Citroën DS. American statement: a 1967 Mustang convertible or mid-century Cadillac.",
      },
      {
        heading: "Timing and logistics",
        body:
          "Most ceremonies book a 4-hour window: 30 minutes' arrival at the bridal address, 30 minutes' transfer to the venue, 2 hours' on-site for photos and the ceremony, 30 minutes' transfer to the reception. Cars usually arrive 30 minutes before the booked pickup, decorated, with the chauffeur briefed on the route.",
      },
      {
        heading: "Costs",
        body:
          "UK and Ireland: £400–£900 for a Silver Shadow or Bentley S2 package, £500–£1,200 for a Jaguar E-Type chauffeured, £200–£500 for a classic Mini or Fiat 500. Mainland Europe: similar ranges in euros. Australia: AU$600–AU$1,400. Multi-car packages and Saturday peak-season weddings cost 15–30% more than midweek bookings.",
      },
    ],
    checklist: [
      "Confirm chauffeur vs self-drive at booking.",
      "Share the venue addresses and timings with the owner two weeks before the date.",
      "Confirm ribbon colour and any decoration requests.",
      "Confirm cover hours and what overtime costs (typically £80–£150 per extra hour).",
      "Share a contact for the day (best man, planner) so the chauffeur can call if delayed.",
      "Plan a wet-weather alternative (umbrella, drop point under cover).",
    ],
    faqs: [
      {
        q: "How far in advance should I book?",
        a: "For a Saturday wedding in peak season (May–September), book 6–9 months ahead. For midweek or off-season, 2–3 months is usually enough.",
      },
      {
        q: "Can I have the car at the reception for the whole evening?",
        a: "Yes. Most operators offer extended cover for an hourly rate. Keeping the car outside the reception for photos through the evening is a popular add-on.",
      },
      {
        q: "What if the car breaks down on the day?",
        a: "Reputable wedding-car operators carry a back-up car for exactly this reason. Always ask, in advance, what the contingency plan is.",
      },
      {
        q: "Is there a minimum age for self-drive on a wedding day?",
        a: "Yes, typically 30 or 35. Each listing states the minimum.",
      },
    ],
    listingsQuery: { rental_type: "event" },
    related: {
      useCases: ["film-and-tv-car-hire", "anniversary-gift-experience"],
      models: ["rolls-royce-silver-shadow", "jaguar-e-type", "mercedes-pagoda", "fiat-500-classic"],
      cities: ["london", "paris", "rome"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "film-and-tv-car-hire",
    title: "Film and TV Picture Car Hire: Guide for Production Companies",
    shortName: "Film & TV picture cars",
    metaTitle: "Classic Film & TV Picture Car Hire: Insurance, Period Notes, Rates",
    metaDescription:
      "Reference guide to hiring classic cars for film and TV: picture car vs background car, insurance and continuity considerations, period-accurate model choices, and rates.",
    tldr:
      "Film and TV picture-car hire splits into hero cars (driven on camera, often by a recognisable cast member) and background cars (parked, dressed to date a scene). BookAclassic supplies both. Production hires need explicit insurance for filming, a release from the owner, and usually a tight period-accuracy brief.",
    hero: {
      image: publicAssetUrl("cars/1055692/legacy-1214533_1280w.webp"),
      alt: "Peugeot 406 film taxi picture car listed for productions",
    },
    sections: [
      {
        heading: "Hero car vs background car",
        body:
          "A hero car is driven on camera, often by a named cast member; it carries the scene. A background car dresses the period by being parked in shot or sometimes driven by a stunt double. The two have different requirements: hero cars must be reliable, presentable on camera, and (often) drivable by someone with a standard licence. Background cars only need to look right.",
      },
      {
        heading: "Period accuracy",
        body:
          "Productions usually specify the year (often as a window: 'pre-1970', 'mid-1960s'). The model year matters less than what's visible on screen: yellow registration plates (UK, post-1973) date a car instantly; chrome bumpers vs rubber bumpers date an MGB; pre- vs post-facelift indicators date a 911. The listing should photograph all four sides plus interior.",
      },
      {
        heading: "Insurance and releases",
        body:
          "Productions must carry their own production insurance covering the car during filming. The owner signs a release for the days the car is on set. BookAclassic's standard rental cover does not extend to commercial filming use; the listing should disclose 'film/TV work permitted' and the production carries the cover.",
      },
      {
        heading: "Daily rates",
        body:
          "UK rates for picture-car hire are typically: background classic £150–£400/day, hero classic £400–£900/day, hero high-value (DB5, RR Phantom) £1,200–£3,000/day plus owner attendance fees. Multi-day blocks usually attract a discount; first-day prep / wrap-day are often half-rate.",
      },
    ],
    checklist: [
      "Disclose 'film/TV use' to the owner at booking.",
      "Provide a production insurance certificate naming the owner as additional insured.",
      "Sign a release covering the dates the car is on set.",
      "Confirm which cast/crew will drive (or whether it stays parked).",
      "Plan transport: many productions wrap and trailer the car to/from set rather than self-driving.",
      "Brief continuity on details such as wheels, plates, badges, and interior items.",
    ],
    faqs: [
      {
        q: "Can I hire a James Bond DB5 for a shoot?",
        a: "Original DB5s are very rarely offered for filming. Most film-spec DB5 work uses well-built tributes. We can introduce production teams to specialist owners who have done film work before.",
      },
      {
        q: "Do you supply stunt-driver-rated cars?",
        a: "Some listings explicitly note that the owner has done stunt or precision driving on set. Filter by 'film/TV experience' in the search or ask the owner directly.",
      },
      {
        q: "What about left-hand vs right-hand drive?",
        a: "Specify in the brief. Many UK owners have RHD; for productions filming as Europe or US, LHD listings exist but are rarer in the UK and IE markets.",
      },
    ],
    listingsQuery: { rental_type: "event" },
    related: {
      useCases: ["wedding-car-hire"],
      models: ["aston-martin-db5", "ford-mustang-1967", "jaguar-e-type"],
      cities: ["london", "paris"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "anniversary-gift-experience",
    title: "Anniversary and Milestone-Birthday Classic Car Gift Experiences",
    shortName: "Anniversary gifts",
    metaTitle: "Classic Car Anniversary & Birthday Gift Experience: How It Works",
    metaDescription:
      "Reference guide to gifting a classic car experience for an anniversary or milestone birthday: how to book, voucher options, and the most-popular gift cars.",
    tldr:
      "Gift bookings are typically a one-day self-drive or chauffeured experience built around a specific classic the recipient already loves. Most operators will accept a date-flexible voucher and let the recipient pick the day within a 12-month window.",
    hero: {
      image: publicAssetUrl("cars/1032644/legacy-1140847_1280w.webp"),
      alt: "Classic Mercedes-Benz 280 SL convertible for a milestone celebration",
    },
    sections: [
      {
        heading: "What makes a good gift booking",
        body:
          "Choose a specific car, usually the recipient's 'dream car', and a route they'll enjoy: a Cotswolds loop, a Highlands drive, or a Côte d'Azur corniche. The best gifts pair a half-day drive with a lunch reservation. Voucher options let the recipient pick the date.",
      },
      {
        heading: "Most-gifted classics by age band",
        body:
          "30–40th birthday: Porsche 911 (964/993), Jaguar XJS, BMW M3 E30. 40–50th: Aston Martin DB7, Mercedes Pagoda, Jaguar E-Type. 50–60th: Rolls-Royce Silver Shadow chauffeured tour, MGB or Triumph TR6 self-drive. Gold/Silver wedding anniversary: chauffeured Bentley or Rolls.",
      },
      {
        heading: "Booking a gift voucher",
        body:
          "Many owners on BookAclassic offer gift vouchers redeemable within 12 months. The buyer specifies the car, drive duration, and any package extras; the recipient picks the date. Useful for surprise gifts where the recipient's calendar isn't yet known.",
      },
    ],
    checklist: [
      "Choose a car the recipient already has an emotional connection with.",
      "Pick a route within 2 hours of home (don't add travel pressure to a gift day).",
      "Confirm voucher validity (most are 12 months).",
      "Confirm the recipient meets the minimum-age and licence requirements for self-drive.",
      "Include a lunch booking if it's a half-day drive.",
    ],
    faqs: [
      {
        q: "Can I buy a voucher without picking a date?",
        a: "Yes. Most owners offer date-flexible vouchers. Pick the car, duration, and any package extras; the recipient chooses the date.",
      },
      {
        q: "What if the recipient can't drive a manual gearbox?",
        a: "Filter to automatic listings (Silver Shadow, Pagoda SL, 280 SL, some 911 Tiptronics) or book chauffeured.",
      },
    ],
    listingsQuery: { rental_type: "driving" },
    related: {
      useCases: ["wedding-car-hire", "driving-experience-hire"],
      models: ["porsche-911-air-cooled", "jaguar-e-type", "mercedes-pagoda"],
      cities: ["cotswolds", "cote-dazur"],
    },
    updatedAt: UPDATED,
  },
  {
    slug: "driving-experience-hire",
    title: "Classic Car Driving Experience Hire: Self-Drive Weekend Tours",
    shortName: "Driving experiences",
    metaTitle: "Classic Car Driving Experience Hire: Self-Drive Weekends Explained",
    metaDescription:
      "Reference guide to hiring a classic car for a self-drive weekend or driving experience: routes, insurance, daily mileage limits, and the most-rented models.",
    tldr:
      "A driving experience is a 1–3 day self-drive hire built around a specific route. Most popular routes: the Cotswolds and Highlands (UK), the Côte d'Azur corniches (FR), the Val d'Orcia (IT), the Great Ocean Road (AU). Daily mileage limits typically apply (150–250 miles/day) above which an excess-mileage fee kicks in.",
    hero: {
      image: publicAssetUrl("cars/1036110/legacy-1208330_1280w.webp"),
      alt: "MGB Roadster available for self-drive hire",
    },
    sections: [
      {
        heading: "How it works",
        body:
          "You pick the car, the dates, and the route; the owner delivers or has the car at their location for collection. For cars located in the United States, self-drive insurance is included in the booking; elsewhere the owner arranges the cover for the hire. Most weekend hires are picked up Friday afternoon and returned Sunday evening.",
      },
      {
        heading: "Insurance, deposits, and licence checks",
        body:
          "For self-drive hires of U.S.-located cars, BookAclassic insurance covers the driver named on the booking for normal road use; track use is excluded. Elsewhere, the cover arranged by the owner applies, so check the listing for its terms. Minimum age is usually 30, sometimes 25 or 35 depending on the car. The security deposit ranges from £500 to £5,000 depending on the car's value; it is pre-authorised on a card and released after return.",
      },
      {
        heading: "Daily mileage and overage",
        body:
          "Most listings include 150–250 miles per day. Beyond that, excess mileage is charged at £0.50–£1.50 per mile depending on the car. For a long touring trip, ask the owner to quote a flat fee for the planned distance up front.",
      },
      {
        heading: "Best route per region",
        body:
          "UK: Cotswolds loop (2 days), Highland 'NC500' for the longer hire (5–7 days), Lake District for a quieter alternative. France: Côte d'Azur three-corniches loop, Alpine cols (Galibier, Iseran). Italy: Val d'Orcia + Chianti, Stelvio Pass, Dolomites. Australia: Great Ocean Road, Tasmania.",
      },
    ],
    checklist: [
      "Pick the car based on the route (small Italian car for a Tuscan loop, GT for a Highland trip).",
      "Confirm minimum age and any second-driver provisions.",
      "Confirm mileage allowance and overage rate.",
      "Plan accommodation with secure overnight parking.",
      "Carry the owner's mobile in case anything goes wrong.",
    ],
    faqs: [
      {
        q: "Can I take the car on a ferry / abroad?",
        a: "Sometimes. Some owners restrict the car to a specific country; others welcome cross-border touring. Confirm in writing before booking and check that the cover in place extends to the destination.",
      },
      {
        q: "What if I can't drive a manual?",
        a: "Filter to automatic listings (Silver Shadow, Pagoda 280 SL with auto, 964/993 Tiptronic). Manuals dominate driving-experience hires though.",
      },
      {
        q: "What's the minimum-age requirement?",
        a: "Typically 30 for most classics; 35 for high-value cars; 25 for entry-level classics (Mini, Beetle, MGB).",
      },
    ],
    listingsQuery: { rental_type: "driving" },
    related: {
      useCases: ["anniversary-gift-experience"],
      models: ["porsche-911-air-cooled", "jaguar-e-type", "mercedes-pagoda", "mgb"],
      cities: ["cotswolds", "tuscany", "cote-dazur"],
    },
    updatedAt: UPDATED,
  },
];

export function getUseCaseGuide(slug: string): UseCaseGuide | undefined {
  return USE_CASE_GUIDES.find((u) => u.slug === slug);
}
